import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Loader2, AlertTriangle, AlertCircle, CheckCircle2, Copy, RefreshCw,
  ChevronDown, ChevronRight, Download, UserPlus, Users,
} from 'lucide-react';

const sevStyles = {
  red:   { bg: 'bg-red-50 border-red-200', text: 'text-red-900', icon: AlertCircle, iconColor: 'text-red-600' },
  amber: { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-900', icon: AlertTriangle, iconColor: 'text-amber-600' },
  green: { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-900', icon: CheckCircle2, iconColor: 'text-emerald-600' },
};

const Section = ({ title, children, defaultOpen = false, testid }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border rounded-lg" data-testid={testid}>
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/40"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="font-medium">{title}</span>
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
      {open && <div className="px-4 pb-4 border-t">{children}</div>}
    </div>
  );
};

const Pct = ({ num, denom }) => {
  if (!denom) return <span className="text-muted-foreground">—</span>;
  const p = Math.round((num * 100) / denom);
  const color = p === 0 ? 'text-red-600' : p < 50 ? 'text-amber-600' : 'text-emerald-700';
  return <span className={`font-mono ${color}`}>{num}/{denom} ({p}%)</span>;
};

const RawJson = ({ data, testid }) => (
  <pre
    data-testid={testid}
    className="bg-slate-950 text-slate-100 text-[11px] rounded p-3 overflow-x-auto max-h-96 whitespace-pre font-mono leading-tight"
  >
    {JSON.stringify(data, null, 2)}
  </pre>
);

const SquareDiagnosticsDialog = ({ open, onOpenChange }) => {
  const { api, currentLocation } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [teamData, setTeamData] = useState(null);
  const [teamLoading, setTeamLoading] = useState(false);
  const [labelDrafts, setLabelDrafts] = useState({}); // team_member_id → draft name
  const [savingId, setSavingId] = useState(null);
  const [resolving, setResolving] = useState(false);

  const load = useCallback(async () => {
    if (!currentLocation) return;
    setLoading(true);
    try {
      const res = await api.get('/square/diagnostics', { params: { location_id: currentLocation.id } });
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load diagnostics');
    } finally {
      setLoading(false);
    }
  }, [api, currentLocation]);

  const loadTeam = useCallback(async () => {
    if (!currentLocation) return;
    setTeamLoading(true);
    try {
      const res = await api.get('/square/team-members', { params: { location_id: currentLocation.id } });
      setTeamData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load team members');
    } finally {
      setTeamLoading(false);
    }
  }, [api, currentLocation]);

  const saveLabel = async (teamMemberId) => {
    const name = (labelDrafts[teamMemberId] || '').trim();
    if (!name) return toast.error('Enter a name first');
    setSavingId(teamMemberId);
    try {
      const res = await api.post('/square/team-members/label', {
        location_id: currentLocation.id,
        team_member_id: teamMemberId,
        name,
      });
      toast.success(`${res.data?.orders_updated || 0} orders updated with "${name}"`);
      setLabelDrafts((d) => ({ ...d, [teamMemberId]: '' }));
      loadTeam();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save name');
    } finally {
      setSavingId(null);
    }
  };

  const resolveAll = async () => {
    setResolving(true);
    try {
      const res = await api.post('/square/resolve-servers', null, {
        params: { location_id: currentLocation.id },
      });
      toast.success(`Re-resolved ${res.data?.orders_updated || 0} orders using ${res.data?.name_map_size || 0} mapped team members`);
      loadTeam();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not resolve');
    } finally {
      setResolving(false);
    }
  };

  useEffect(() => {
    if (open) { load(); loadTeam(); }
  }, [open, load, loadTeam]);

  const copyAll = () => {
    if (!data) return;
    navigator.clipboard?.writeText(JSON.stringify(data, null, 2));
    toast.success('Full diagnostics copied to clipboard');
  };

  const download = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `square-diagnostics-${currentLocation?.name || 'location'}-${new Date().toISOString().slice(0,10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const s = data?.summary;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="square-diagnostics-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>Square diagnostics — raw DB view</DialogTitle>
            <div className="flex gap-2 mr-6">
              <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="diag-refresh">
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              </Button>
              <Button size="sm" variant="outline" onClick={copyAll} disabled={!data} data-testid="diag-copy">
                <Copy className="w-3 h-3 mr-1" /> Copy JSON
              </Button>
              <Button size="sm" variant="outline" onClick={download} disabled={!data} data-testid="diag-download">
                <Download className="w-3 h-3 mr-1" /> Download
              </Button>
            </div>
          </div>
        </DialogHeader>

        {loading && !data ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : !data ? (
          <div className="text-sm text-muted-foreground py-6 text-center">No diagnostics loaded.</div>
        ) : (
          <div className="space-y-4">
            {/* Summary strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="diag-summary">
              <SummaryBox label="Connections" value={s.connections_this_location} warn={s.connections_this_location === 0} />
              <SummaryBox label="Active?" value={s.active_connection ? 'Yes' : 'No'} warn={!s.active_connection} />
              <SummaryBox label="Square orders in DB" value={s.orders_square} warn={s.orders_square === 0} />
              <SummaryBox label="Sync runs logged" value={s.sync_runs_logged} warn={s.sync_runs_logged === 0} />
              <SummaryBox label="All orders (any source)" value={s.orders_total} />
              <SummaryBox label="Abandoned OAuth" value={s.abandoned_oauth_attempts} warn={s.abandoned_oauth_attempts > 0} />
              <SummaryBox label="Mapped team members" value={s.mapped_team_members} warn={s.orders_square > 0 && s.mapped_team_members === 0} />
              <SummaryBox label="Revenue rows (Square)" value={s.revenue_daily_square_rows} warn={s.orders_square > 0 && s.revenue_daily_square_rows === 0} />
            </div>

            {/* Issues */}
            {data.issues.length === 0 ? (
              <div className={`border rounded-lg px-4 py-3 flex items-start gap-2 ${sevStyles.green.bg} ${sevStyles.green.text}`} data-testid="diag-no-issues">
                <CheckCircle2 className={`w-4 h-4 mt-0.5 ${sevStyles.green.iconColor}`} />
                <div><strong>No issues flagged.</strong> Data looks healthy for this location.</div>
              </div>
            ) : (
              <div className="space-y-2" data-testid="diag-issues">
                <div className="text-sm font-medium">Flagged issues ({data.issues.length})</div>
                {data.issues.map((iss, i) => {
                  const style = sevStyles[iss.severity] || sevStyles.amber;
                  const Icon = style.icon;
                  return (
                    <div key={i} className={`border rounded-lg px-4 py-3 flex items-start gap-2 ${style.bg} ${style.text}`}>
                      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${style.iconColor}`} />
                      <div className="flex-1">
                        <div className="font-semibold">{iss.title}</div>
                        <div className="text-sm mt-1">{iss.detail}</div>
                        {iss.fix_hint && (
                          <div className="text-xs mt-2 italic opacity-80"><strong>Suggested fix:</strong> {iss.fix_hint}</div>
                        )}
                      </div>
                      <Badge variant="outline" className="uppercase text-[10px]">{iss.severity}</Badge>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Field coverage */}
            <Section title={`Field coverage on Square orders (${s.orders_square})`} defaultOpen testid="diag-section-coverage">
              <table className="w-full text-sm mt-3">
                <thead className="text-left text-muted-foreground border-b">
                  <tr><th className="py-2">Field</th><th>Populated</th></tr>
                </thead>
                <tbody>
                  {data.orders_coverage.map((c) => (
                    <tr key={c.field} className="border-b last:border-0">
                      <td className="py-2 font-mono text-xs">{c.field}</td>
                      <td><Pct num={c.populated} denom={c.total} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>

            {/* Team Member Mapping */}
            <Section
              title={`Team Member Mapping (${teamData?.team_members_on_orders?.length || 0} Square IDs seen on orders)`}
              defaultOpen
              testid="diag-section-team-mapping"
            >
              <div className="mt-3 space-y-3">
                <div className="flex items-start gap-2 text-xs text-muted-foreground">
                  <Users className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    Square tags each order with an opaque <code className="font-mono">team_member_id</code> like
                    <code className="font-mono"> TMr_BKpnTqyeHVR4</code>. Give each one a human name below and
                    we'll backfill every past order it appears on, plus pick it up on future syncs.
                  </div>
                </div>

                {teamLoading && !teamData ? (
                  <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin" /></div>
                ) : !teamData ? null : (
                  <>
                    {teamData.team_members_on_orders.length === 0 ? (
                      <p className="text-xs text-muted-foreground py-2">
                        No Square IDs are indexed on your orders yet. If you have historical Square orders in the DB,
                        click <strong>Re-run server resolution on all orders</strong> below — we'll extract each
                        order's <code className="font-mono">team_member_id</code> from its raw Square payload and
                        populate this table so you can label them.
                        {teamData.orders_without_team_member_id > 0 && (
                          <> {teamData.orders_without_team_member_id} order(s) are currently indexed as having no team_member_id.</>
                        )}
                      </p>
                    ) : (
                      <table className="w-full text-sm" data-testid="team-mapping-table">
                        <thead className="text-left text-muted-foreground border-b">
                          <tr>
                            <th className="py-2">Square ID</th>
                            <th>Orders</th>
                            <th>Current name</th>
                            <th>Set / change name</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {teamData.team_members_on_orders.map((t) => (
                            <tr key={t.team_member_id} className="border-b last:border-0" data-testid={`team-row-${t.team_member_id}`}>
                              <td className="py-2 font-mono text-[10px]">{t.team_member_id}</td>
                              <td className="font-mono">{t.order_count}</td>
                              <td>
                                {t.name ? (
                                  <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200">{t.name}</Badge>
                                ) : (
                                  <span className="text-xs text-amber-700">Unlabelled</span>
                                )}
                              </td>
                              <td className="pr-2">
                                <Input
                                  placeholder={t.name || 'e.g. Sian H.'}
                                  value={labelDrafts[t.team_member_id] || ''}
                                  onChange={(e) => setLabelDrafts((d) => ({ ...d, [t.team_member_id]: e.target.value }))}
                                  className="h-8 text-xs"
                                  data-testid={`team-name-input-${t.team_member_id}`}
                                />
                              </td>
                              <td>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={savingId === t.team_member_id || !(labelDrafts[t.team_member_id] || '').trim()}
                                  onClick={() => saveLabel(t.team_member_id)}
                                  data-testid={`team-save-${t.team_member_id}`}
                                >
                                  {savingId === t.team_member_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />}
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t">
                      <div className="text-xs text-muted-foreground">
                        {(teamData.known_employees || []).length} Square-synced employee(s) in <code>employees</code>.
                        {teamData.orders_without_team_member_id > 0 && (
                          <> · <span className="text-amber-700">{teamData.orders_without_team_member_id} order(s) have no team_member_id captured.</span></>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={resolveAll}
                        disabled={resolving}
                        data-testid="resolve-servers-btn"
                      >
                        {resolving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                        Re-run server resolution on all orders
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </Section>

            {/* Provider breakdown */}
            <Section title="Orders by provider (all sources)" testid="diag-section-providers">
              <table className="w-full text-sm mt-3">
                <tbody>
                  {data.provider_breakdown.map((p) => (
                    <tr key={p.provider} className="border-b last:border-0">
                      <td className="py-2 font-mono text-xs">{p.provider}</td>
                      <td className="text-right font-mono">{p.count}</td>
                    </tr>
                  ))}
                  {data.provider_breakdown.length === 0 && (
                    <tr><td className="py-2 text-muted-foreground text-xs">No orders yet.</td></tr>
                  )}
                </tbody>
              </table>
            </Section>

            {/* Connections */}
            <Section title={`Square connections (${data.connections.length})`} testid="diag-section-connections">
              <RawJson data={data.connections} testid="diag-connections-raw" />
            </Section>

            {/* Sync runs */}
            <Section title={`Recent sync runs (${data.recent_sync_runs.length})`} testid="diag-section-syncruns">
              {data.recent_sync_runs.length === 0 ? (
                <p className="text-sm text-muted-foreground mt-3">No sync runs logged.</p>
              ) : (
                <table className="w-full text-xs mt-3">
                  <thead className="text-left text-muted-foreground border-b">
                    <tr><th className="py-2">Resource</th><th>Status</th><th>Records</th><th>Started</th><th>Error</th></tr>
                  </thead>
                  <tbody>
                    {data.recent_sync_runs.map((r, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-1.5 capitalize">{(r.resource || '').replace('_', ' ')}</td>
                        <td className={r.status === 'ok' ? 'text-emerald-700' : r.status === 'error' ? 'text-red-700' : 'text-amber-700'}>{r.status}</td>
                        <td className="font-mono">{r.records_upserted ?? '—'}</td>
                        <td className="text-muted-foreground">{r.started_at?.slice(0, 16).replace('T', ' ')}</td>
                        <td className="text-red-700 text-[11px]">{r.error?.slice(0, 80)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Section>

            {/* Sample orders */}
            <Section
              title={`Sample orders (${data.sample_orders.length}) — raw payload as stored`}
              defaultOpen
              testid="diag-section-samples"
            >
              <div className="text-xs text-muted-foreground mb-2 mt-3">
                These are the most recent orders in the DB for this location. If 'provider' isn't 'square',
                the data on Front of House is coming from a different source (e.g., Toast CSV upload).
              </div>
              {data.sample_orders.length === 0 ? (
                <p className="text-sm text-muted-foreground">No orders in the DB for this location.</p>
              ) : (
                <div className="space-y-2">
                  {data.sample_orders.map((o, i) => (
                    <details key={o.id || i} className="border rounded">
                      <summary className="cursor-pointer px-3 py-2 text-xs flex items-center gap-3 bg-muted/30">
                        <span className="font-mono">{(o.id || '').slice(0, 8)}</span>
                        <Badge variant="outline" className="text-[10px]">{o.provider || o.pos_provider || 'no-provider'}</Badge>
                        <span className="text-muted-foreground">{o.closed_at || o.opened_at || o.date}</span>
                        <span className="ml-auto font-mono">£{(o.amount ?? o.total ?? 0).toFixed?.(2) ?? o.amount}</span>
                      </summary>
                      <div className="p-2">
                        <RawJson data={o} testid={`diag-order-raw-${i}`} />
                      </div>
                    </details>
                  ))}
                </div>
              )}
            </Section>

            {/* Pending OAuth */}
            {data.pending_oauth_states.length > 0 && (
              <Section title={`Pending/abandoned OAuth states (${data.pending_oauth_states.length})`} testid="diag-section-oauth">
                <RawJson data={data.pending_oauth_states} testid="diag-oauth-raw" />
              </Section>
            )}

            {/* Revenue daily */}
            <Section title={`revenue_daily rows (Square, latest 10)`} testid="diag-section-revenue">
              <RawJson data={data.revenue_daily_sample} testid="diag-revenue-raw" />
            </Section>

            <p className="text-xs text-muted-foreground pt-2">
              Generated {data.generated_at?.slice(0, 19).replace('T', ' ')} UTC · env: {data.env} ·
              org {data.org_id?.slice(0, 8)} · location {data.location_id?.slice(0, 8)}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const SummaryBox = ({ label, value, warn }) => (
  <div className={`border rounded-lg p-3 ${warn ? 'border-amber-300 bg-amber-50' : 'bg-muted/30'}`}>
    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className={`text-xl font-bold mt-1 ${warn ? 'text-amber-700' : ''}`}>{value}</div>
  </div>
);

export default SquareDiagnosticsDialog;
